import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';

const ExpertSupportContext = createContext();

export function useExpertSupport() {
  return useContext(ExpertSupportContext);
}

// Sample professor data to use if Firestore data is not available
const defaultProfessors = [
  {
    id: 'prof1',
    name: 'Dr. Sarah Johnson',
    specialization: 'Computer Science',
    university: 'Tech University',
    rating: 4.8,
    courses: ['Data Structures', 'Algorithms', 'Web Development'],
    availability: [new Date().toISOString()]
  },
  {
    id: 'prof2',
    name: 'Prof. Michael Chen',
    specialization: 'Mathematics',
    university: 'Science Academy',
    rating: 4.7,
    courses: ['Calculus', 'Linear Algebra', 'Discrete Mathematics'],
    availability: [new Date().toISOString()]
  }
];

export function ExpertSupportProvider({ children }) {
  const [professors, setProfessors] = useState(defaultProfessors);
  const [supportSessions, setSupportSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dbInitialized, setDbInitialized] = useState(true);

  // Check if Firebase is initialized
  useEffect(() => {
    try {
      // Simple test to check if db is properly initialized
      if (!db || !collection) {
        setDbInitialized(false);
        setError("Firebase database not properly initialized");
        setLoading(false);
      }
    } catch (err) {
      console.error("Firebase initialization error:", err);
      setDbInitialized(false);
      setError("Firebase initialization error");
      setLoading(false);
    }
  }, []);

  // Fetch professors and support sessions
  useEffect(() => {
    // Don't try to fetch if db is not initialized
    if (!dbInitialized) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const [professorsSnapshot, supportSessionsSnapshot] = await Promise.all([
          getDocs(collection(db, 'professors')),
          getDocs(collection(db, 'supportSessions'))
        ]);

        // Only update professors if we got data from Firestore
        const firestoreProfessors = professorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (firestoreProfessors.length > 0) {
          setProfessors(firestoreProfessors);
        } // Otherwise keep the default professors

        setSupportSessions(supportSessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setError(err.message);
        console.error("Error fetching expert support data:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dbInitialized]);

  // Create support request
  const createSupportRequest = async (requestData) => {
    if (!dbInitialized) {
      console.error("Cannot create support request: Database not initialized");
      return { id: 'mock-request-id', ...requestData };
    }
    
    try {
      setLoading(true);
      const docRef = await addDoc(collection(db, 'supportRequests'), {
        ...requestData,
        status: 'pending',
        createdAt: new Date(),
        assignedExpert: null
      });
      return { id: docRef.id, ...requestData };
    } catch (err) {
      setError(err.message);
      console.error("Error creating support request:", err.message);
      // Return mock data to prevent UI breaks
      return { id: 'mock-request-id', ...requestData };
    } finally {
      setLoading(false);
    }
  };

  // Wait for expert assignment
  const waitForExpert = async (requestId) => {
    if (!dbInitialized) {
      console.error("Cannot wait for expert: Database not initialized");
      return professors[0]; // Return first default professor
    }
    
    try {
      setLoading(true);
      const requestRef = doc(db, 'supportRequests', requestId);
      const requestDoc = await getDoc(requestRef);
      
      if (!requestDoc.exists()) {
        console.warn("Request not found, returning default expert");
        return professors[0]; // Return first default professor
      }

      const requestData = requestDoc.data();
      if (!requestData.assignedExpert) {
        // In a real application, you would implement a more sophisticated
        // expert assignment system here
        const availableExpert = professors[0];
        await updateDoc(requestRef, { assignedExpert: availableExpert.id });
        return availableExpert;
      }

      return professors.find(p => p.id === requestData.assignedExpert) || professors[0];
    } catch (err) {
      setError(err.message);
      console.error("Error waiting for expert:", err.message);
      return professors[0]; // Return first default professor
    } finally {
      setLoading(false);
    }
  };

  // Initialize session
  const initializeSession = async (requestId, expertId) => {
    if (!dbInitialized) {
      console.error("Cannot initialize session: Database not initialized");
      return { id: 'mock-session-id', requestId, expertId };
    }
    
    try {
      setLoading(true);
      const docRef = await addDoc(collection(db, 'supportSessions'), {
        requestId,
        expertId,
        status: 'active',
        startedAt: new Date(),
        messages: []
      });
      return { id: docRef.id, requestId, expertId };
    } catch (err) {
      setError(err.message);
      console.error("Error initializing session:", err.message);
      return { id: 'mock-session-id', requestId, expertId };
    } finally {
      setLoading(false);
    }
  };

  // Initialize chat
  const initializeChat = async (sessionId) => {
    if (!dbInitialized) {
      console.error("Cannot initialize chat: Database not initialized");
      return { id: sessionId, messages: [] };
    }
    
    try {
      setLoading(true);
      const sessionRef = doc(db, 'supportSessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        console.warn("Session not found, returning empty chat");
        return { id: sessionId, messages: [] };
      }

      return sessionDoc.data();
    } catch (err) {
      setError(err.message);
      console.error("Error initializing chat:", err.message);
      return { id: sessionId, messages: [] };
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const sendMessage = async (sessionId, message) => {
    if (!dbInitialized) {
      console.error("Cannot send message: Database not initialized");
      return;
    }
    
    try {
      setLoading(true);
      const sessionRef = doc(db, 'supportSessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        console.warn("Session not found for message send");
        return;
      }
      
      await updateDoc(sessionRef, {
        messages: [...sessionDoc.data().messages, message]
      });
    } catch (err) {
      setError(err.message);
      console.error("Error sending message:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // Upload file
  const uploadFile = async (file) => {
    try {
      setLoading(true);
      // In a real application, you would implement file upload to storage
      // For now, we'll just return a mock file object
      return {
        id: Date.now(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file)
      };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Remove attachment
  const removeAttachment = (fileId) => {
    // In a real application, you would also delete the file from storage
    return fileId;
  };

  // Create support session
  const createSupportSession = async (data) => {
    try {
      setLoading(true);
      const docRef = await addDoc(collection(db, 'supportSessions'), {
        ...data,
        createdAt: new Date(),
        status: 'scheduled',
        feedback: null
      });
      return docRef.id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update support session
  const updateSupportSession = async (id, data) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'supportSessions', id), data);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add professor feedback
  const addProfessorFeedback = async (sessionId, feedback) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'supportSessions', sessionId), {
        feedback,
        updatedAt: new Date()
      });
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get professor availability - Make this function more resilient
  const getProfessorAvailability = async (professorId) => {
    if (!dbInitialized) {
      console.error("Cannot get professor availability: Database not initialized");
      return [new Date().toISOString()]; // Return a default time
    }
    
    try {
      setLoading(true);
      const sessionsQuery = query(
        collection(db, 'supportSessions'),
        where('professorId', '==', professorId),
        where('status', '==', 'scheduled')
      );
      const snapshot = await getDocs(sessionsQuery);
      const times = snapshot.docs.map(doc => doc.data().scheduledTime);
      return times.length > 0 ? times : [new Date().toISOString()]; // Return default if none found
    } catch (err) {
      setError(err.message);
      console.error("Error getting professor availability:", err.message);
      return [new Date().toISOString()]; // Return a default time on error
    } finally {
      setLoading(false);
    }
  };

  const value = {
    professors,
    supportSessions,
    loading,
    error,
    createSupportSession,
    updateSupportSession,
    addProfessorFeedback,
    getProfessorAvailability,
    createSupportRequest,
    waitForExpert,
    initializeSession,
    initializeChat,
    sendMessage,
    uploadFile,
    removeAttachment
  };

  return (
    <ExpertSupportContext.Provider value={value}>
      {children}
    </ExpertSupportContext.Provider>
  );
} 